// Hall body composition model — simplified version of Hall 2011
// "Predicting metabolic adaptation, body weight change, and energy intake in humans"
// Kevin Hall, NIDDK. Powers the NIH Body Weight Planner.
//
// Two-compartment day-by-day simulation: fat mass + lean mass evolve based on
// daily energy balance, with adaptive thermogenesis (β) and dynamic Forbes
// partitioning. Inverse-solved via bisection to find the intake that hits a
// target weight by a target date.

const RHO_FAT = 9500;     // kcal/kg adipose
const RHO_LEAN = 1100;    // kcal/kg lean tissue (mostly water)
const FORBES_C = 10.4;    // Forbes constant (kg)
const GAMMA_F = 3.2;      // RMR contribution per kg fat (kcal/kg/day)
const GAMMA_L = 22;       // RMR contribution per kg lean (kcal/kg/day)
const TEF_FRAC = 0.10;    // thermic effect of food
const BETA = 0.24;        // adaptive thermogenesis coefficient

const ACTIVITY_MULT = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

function katchMcArdleBMR(lbm) {
  return 370 + 21.6 * lbm;
}

// Simulate `days` days at constant `intake`. Returns end state + trajectory.
export function simulateHall({ weight, bfPct, activity, intake, days }) {
  let FM = weight * (bfPct / 100);
  let FFM = weight - FM;

  // Baseline maintenance (Katch-McArdle × activity multiplier)
  const baselineBMR = katchMcArdleBMR(FFM);
  const baselineTDEE = baselineBMR * (ACTIVITY_MULT[activity] || 1.55);
  const baselineIntake = baselineTDEE; // assume currently weight-stable

  // K calibration: at t=0, EE must equal baselineTDEE.
  // EE(0) = K + γF·FM₀ + γL·FFM₀ + TEF·intake₀ + β·(intake₀ − baseline) → β term = 0
  const K = baselineTDEE - GAMMA_F * FM - GAMMA_L * FFM - TEF_FRAC * baselineIntake;

  const trajectory = [{ day: 0, weight, FM, FFM }];

  for (let d = 1; d <= days; d++) {
    // Forbes gives ΔFFM/ΔBW (a weight partition). Convert to an energy
    // partition before applying to dE, since lean tissue has much lower
    // energy density than fat tissue (1100 vs 9500 kcal/kg).
    const pW = FORBES_C / (FORBES_C + FM);
    const pE = (RHO_LEAN * pW) / (RHO_LEAN * pW + RHO_FAT * (1 - pW));
    const EE = K + GAMMA_F * FM + GAMMA_L * FFM + TEF_FRAC * intake + BETA * (intake - baselineIntake);
    const dE = intake - EE;
    FM += ((1 - pE) * dE) / RHO_FAT;
    FFM += (pE * dE) / RHO_LEAN;
    // Physiological floors — prevents the sim blowing up on absurd intakes
    if (FM < 0.5) FM = 0.5;
    if (FFM < 30) FFM = 30;
    if (d % 7 === 0 || d === days) {
      trajectory.push({ day: d, weight: FM + FFM, FM, FFM });
    }
  }

  const finalEE = K + GAMMA_F * FM + GAMMA_L * FFM + TEF_FRAC * intake + BETA * (intake - baselineIntake);

  return {
    endWeight: FM + FFM,
    endFM: FM,
    endFFM: FFM,
    endBfPct: (FM / (FM + FFM)) * 100,
    baselineTDEE: Math.round(baselineTDEE),
    finalEE: Math.round(finalEE),
    trajectory,
  };
}

// Bisection: find the constant daily intake that hits the goal in `days`.
// goalMode: 'weight' | 'bf' | 'both'
//   - 'weight': bisect to land at goalWeight; goalBfPct ignored
//   - 'bf':     bisect to land at goalBfPct; goalWeight predicted
//   - 'both':   bisect to land at goalBfPct, then flag if predicted weight ≠ goalWeight
export function solveHallPlan({ weight, bfPct, sex, activity, goalWeight, goalBfPct, goalDate, goalMode = 'weight' }) {
  if (!weight || !bfPct || !goalDate) return null;
  if (goalMode === 'weight' && !goalWeight) return null;
  if ((goalMode === 'bf' || goalMode === 'both') && !goalBfPct) return null;
  if (goalMode === 'both' && !goalWeight) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(goalDate);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (days <= 0) return { error: 'invalidDate' };

  const startFM = weight * (bfPct / 100);
  const startFFM = weight - startFM;
  const startBMR = katchMcArdleBMR(startFFM);
  const startTDEE = Math.round(startBMR * (ACTIVITY_MULT[activity] || 1.55));

  // Bracket — wide enough to handle aggressive cuts and bulks
  let lo = 600;
  let hi = 7000;
  let mid = startTDEE;
  let sim = null;
  let achievable = true;

  // Pick the metric we're bisecting on. For BF% goals: lower intake → lower end BF%
  // (more fat lost), so the direction relative to "too high vs too low" is the same
  // as for weight goals when losing, but we need to be explicit.
  const targetMetric = goalMode === 'weight' ? 'weight' : 'bf';
  const targetValue = goalMode === 'weight' ? goalWeight : goalBfPct;

  for (let i = 0; i < 40; i++) {
    mid = (lo + hi) / 2;
    sim = simulateHall({ weight, bfPct, activity, intake: mid, days });
    const current = targetMetric === 'weight' ? sim.endWeight : sim.endBfPct;
    const tol = targetMetric === 'weight' ? 0.02 : 0.05;
    if (Math.abs(current - targetValue) < tol) break;
    if (current > targetValue) hi = mid; // need to eat less
    else lo = mid;                        // need to eat more
  }

  // If bisection couldn't converge near the bracket edges, the goal isn't achievable
  const finalCurrent = targetMetric === 'weight' ? sim.endWeight : sim.endBfPct;
  const convergeTol = targetMetric === 'weight' ? 0.5 : 0.5;
  if (sim && Math.abs(finalCurrent - targetValue) > convergeTol) {
    achievable = false;
  }

  const intake = Math.round(mid);
  const dailyDelta = intake - startTDEE;
  // For weight/bf modes, predicted end weight comes from the sim
  const predictedEndWeight = +sim.endWeight.toFixed(1);
  const deltaWeight = +(predictedEndWeight - weight).toFixed(1);
  const weeks = days / 7;
  const kgPerWeek = +(deltaWeight / weeks).toFixed(2);
  const pctBwPerWeek = +((Math.abs(kgPerWeek) / weight) * 100).toFixed(2);

  const fmChange = +(sim.endFM - startFM).toFixed(2);
  const lbmChange = +(sim.endFFM - startFFM).toFixed(2);
  const endBfPct = +sim.endBfPct.toFixed(1);

  // For 'both' mode: flag if predicted weight diverges from coach's target
  let weightMismatch = null;
  if (goalMode === 'both' && goalWeight) {
    const diff = predictedEndWeight - goalWeight;
    if (Math.abs(diff) > 1) {
      weightMismatch = { predicted: predictedEndWeight, target: goalWeight, diff: +diff.toFixed(1) };
    }
  }

  // Safety
  const floor = sex === 'female' ? 1200 : 1500;
  const lbmLossShare = deltaWeight < 0 && lbmChange < 0
    ? Math.abs(lbmChange) / Math.abs(deltaWeight)
    : 0;
  let safety = 'ok';
  let safetyReason = null;
  if (!achievable) { safety = 'danger'; safetyReason = 'notAchievable'; }
  else if (intake < floor) { safety = 'danger'; safetyReason = 'belowFloor'; }
  else if (pctBwPerWeek > 1) { safety = 'danger'; safetyReason = 'tooFast'; }
  else if (pctBwPerWeek > 0.75 && lbmLossShare > 0.35) { safety = 'danger'; safetyReason = 'muscleLoss'; }
  else if (pctBwPerWeek > 0.75) { safety = 'warn'; safetyReason = 'aggressive'; }

  return {
    days,
    weeks: +weeks.toFixed(1),
    bmr: Math.round(startBMR),
    tdee: startTDEE,
    requiredIntake: intake,
    dailyDelta,
    deltaWeight,
    kgPerWeek,
    pctBwPerWeek,
    lbmChange,
    fmChange,
    endBfPct,
    endLbm: +sim.endFFM.toFixed(1),
    endFm: +sim.endFM.toFixed(1),
    finalTDEE: sim.finalEE,
    trajectory: sim.trajectory,
    safety,
    safetyReason,
    achievable,
    predictedEndWeight,
    weightMismatch,
    goalMode,
  };
}
